import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSize,
  ButtonSmallSubtleIcon,
  GenericButton,
  GenericCheckBox,
  GenericDropdown,
  GenericDropdownContext,
  Icon,
  IconSVG,
  InputState,
  type ListItemProps,
} from "@studio3t/generic-components";
import { type ElementType, useState } from "react";
import styled, { useTheme } from "styled-components";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { useTestSuites } from "@/hooks/useApi";
import { SampleLink } from "./JsonDocumentEditor";
import { NameModal } from "./NameModal";
import { useLabeledButton } from "./PipelineNavBar";
import {
  assertionValueDisplay,
  freshAssertionDraft,
  meaningfulAssertions,
  TEST_OPERATOR_LABELS,
  TESTS_EDITOR_ID,
  type TestAssertionDraft,
  type TestAssertionOp,
  usePipelineStaging,
} from "./pipelineStaging";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { useGenerateSuiteTests } from "./useGenerateSuiteTests";

// The Tests tab's editor panel. With the pseudo-row selected it offers the
// link face of Figma Bridge_PipelineCode (14785-273097): "Add a new
// test suite" naming a fresh catalog suite, led by "Add a new test" and
// "Generate a test" while a suite is staged. New tests themselves
// come from the staged suite row's Plus — the structure panel's "Untitled
// test*" row (14790-130430, like the transform editor's Untitled script) —
// which swaps the links for the assertion editor (14790-130431):
// Bridge_TestLine rows (field, operator, expected value, remove) over an
// "Add assertion" button. A dropped or plain-clicked test shows the
// viewing face instead (14785-268156): its saved assertions as read-only
// Bridge_TestLine text.

// "suite" is an address keyword to autofill classifiers (Apt/Suite/Unit):
// next to a "Name" field it makes password managers offer identity fill,
// and they read the VISIBLE copy, so NameModal's attribute opt-outs
// can't win. A zero-width space inside the word breaks their keyword
// match while rendering identically; screen readers ignore ZWSP.
const UNMATCHABLE_SUITE = "s\u200Buite";

const OPERATOR_OPTIONS: ReadonlyArray<{
  value: TestAssertionOp;
  label: string;
}> = (
  Object.entries(TEST_OPERATOR_LABELS) as Array<[TestAssertionOp, string]>
).map(([value, label]) => ({ value, label }));

export function TestsEditorPanel() {
  const { editsLocked } = useStudioPipeline();
  // null = the link placeholder; a list = the assertion editor.
  const {
    testDraft: rows,
    setTestDraft: setRows,
    testViewOnly,
    testEditorId,
    testAssertions,
    strictModeOf,
    setStrictMode,
  } = usePipelineStaging();

  if (testViewOnly && testEditorId !== null) {
    return (
      <TestViewer
        rows={testAssertions[testEditorId] ?? []}
        strict={strictModeOf(testEditorId)}
      />
    );
  }

  if (rows === null) {
    return <LinksFace />;
  }

  const patchRow = (key: number, patch: Partial<TestAssertionDraft>) =>
    setRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // Removing the only line clears it instead — the editor always keeps at
  // least one assertion line on screen (the Figma "New test" state).
  const removeRow = (key: number) =>
    setRows(
      rows.length === 1
        ? [freshAssertionDraft()]
        : rows.filter((r) => r.key !== key),
    );

  return (
    <Panel data-testid="tests-editor-panel">
      <Lines>
        {rows.map((row) => (
          <AssertionLine
            key={row.key}
            row={row}
            onPatch={(patch) => patchRow(row.key, patch)}
            onRemove={() => removeRow(row.key)}
          />
        ))}
      </Lines>
      <AddAssertionBlock>
        <GenericButton
          text="Add assertion"
          type="secondary"
          size={ButtonSize.Small}
          icon={Icon.Plus}
          disabled={editsLocked}
          onClick={() => setRows([...rows, freshAssertionDraft()])}
        />
        {/* The old /studio's per-test flag: the engine fails a strict case
            on any output field no assertion covers. Toggling it dirties
            the row like an edited line; Save stores it with the case. */}
        {testEditorId !== null && (
          <StrictToggle
            title="Fail if output has fields not covered by assertions"
            data-testid="test-strict-mode"
          >
            <GenericCheckBox
              label="Strict mode"
              value={strictModeOf(testEditorId)}
              disabled={editsLocked}
              onChange={(value) => setStrictMode(testEditorId, value)}
            />
          </StrictToggle>
        )}
      </AddAssertionBlock>
    </Panel>
  );
}

// The viewing face (Figma 14785-268156): the selected test's saved
// assertions as read-only Bridge_TestLine rows — field and operator in
// textlight, the value's JSON form in textheading. Untouched blank lines
// (an assertion-less case's fresh line) don't render; an assertion-less
// test shows the old /studio's empty message instead of a blank panel.
function TestViewer({
  rows,
  strict,
}: {
  rows: TestAssertionDraft[];
  strict: boolean;
}) {
  const assertions = meaningfulAssertions(rows);
  return (
    <Panel data-testid="tests-editor-panel">
      <Lines data-testid="tests-viewer">
        {assertions.length === 0 && (
          <EmptyViewerNote>No assertions defined</EmptyViewerNote>
        )}
        {strict && (
          <EmptyViewerNote data-testid="tests-viewer-strict">
            Strict mode — fails on fields not covered by assertions
          </EmptyViewerNote>
        )}
        {assertions.map((row) => (
          <ViewLine key={row.key} data-testid="test-view-line">
            <ViewDim>{row.path}</ViewDim>
            <ViewDim>{TEST_OPERATOR_LABELS[row.op]}</ViewDim>
            <ViewValue>{assertionValueDisplay(row.value)}</ViewValue>
          </ViewLine>
        ))}
      </Lines>
    </Panel>
  );
}

// The staged suite shape as usePipelineStaging exposes it — mirrored here
// (rather than imported) since PipelineStagingValue stays private to
// pipelineStaging.tsx.
type StagedSuite = { suiteId: string | null; name: string } | null;

// One Figma link row: right chevron + label. The wand link reuses it via
// `as` for its extra disabled/hover styling (see GenerateLink).
function IconLink({
  as: Tag = SampleLink,
  testId,
  label,
  color,
  disabled,
  onClick,
}: {
  as?: ElementType;
  testId: string;
  label: string;
  color: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Tag
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
    >
      <IconSVG icon={Icon.Right} size={16} colors={{ default: color }} />
      {label}
    </Tag>
  );
}

// The wand link (Figma's "Generate a test"): grayed out and inert while a
// batch is in flight, in both the empty-suite and staged-suite faces.
function GenerateTestLink({
  generation,
}: {
  generation: ReturnType<typeof useGenerateSuiteTests>;
}) {
  const { editsLocked } = useStudioPipeline();
  const theme = useTheme();
  return (
    <IconLink
      as={GenerateLink}
      testId="tests-generate-link"
      label="Generate a test"
      color={
        generation.generating ? theme.semanticTextPlaceholder : theme.linkColor
      }
      disabled={generation.generating || editsLocked}
      onClick={generation.generate}
    />
  );
}

// The staged-suite face's own two links ("Add a new test" + the wand) —
// they only show while the staged group holds the selection
// (TESTS_EDITOR_ID); any other selection over a non-empty staged suite
// goes straight to "Add a new test suite" below.
function StagedSuiteLinks({
  stagedSuite,
  testEditorId,
  openNewTestDraft,
  generation,
}: {
  stagedSuite: StagedSuite;
  testEditorId: string | null;
  openNewTestDraft: () => void;
  generation: ReturnType<typeof useGenerateSuiteTests>;
}) {
  const { editsLocked } = useStudioPipeline();
  const theme = useTheme();
  if (stagedSuite === null || testEditorId !== TESTS_EDITOR_ID) return null;
  return (
    <>
      <IconLink
        as={GenerateLink}
        testId="tests-add-test-link"
        label="Add a new test"
        color={editsLocked ? theme.semanticTextPlaceholder : theme.linkColor}
        disabled={editsLocked}
        onClick={openNewTestDraft}
      />
      <GenerateTestLink generation={generation} />
    </>
  );
}

// True while the staged suite has NO tests yet — the "New test first"
// face (just "Write a test" + the wand); the first saved test flips the
// panel to the suite-link face.
function isEmptyStagedSuite(stagedSuite: StagedSuite, testCount: number) {
  return stagedSuite !== null && testCount === 0;
}

// Replacing a staged suite with unsaved work needs the confirm gate
// first (see LinksFace's note); otherwise the name modal opens straight
// away.
function chooseSuiteNamingAction(
  stagedSuite: StagedSuite,
  hasDirtyTestWork: () => boolean,
  openConfirm: () => void,
  openNaming: () => void,
) {
  if (stagedSuite && hasDirtyTestWork()) {
    openConfirm();
  } else {
    openNaming();
  }
}

// The Add-suite modal's name-collision check: a case-insensitive match
// against the catalog's existing suite names.
function suiteNameValidation(
  suites: ReadonlyArray<{ name: string }> | undefined,
  trimmed: string,
) {
  const taken = (suites ?? []).some(
    (s) => s.name.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  return taken
    ? {
        state: InputState.Warning,
        messages: ["A test suite with this name already exists"],
      }
    : null;
}

// The links face (pseudo-row selected, Figma 14785-273097): "Add a new
// test suite" names a NEW suite (modal 15013-212374) — Add stages it as
// a PENDING group only; the catalog row (and with it the tree entry)
// lands with the suite's first non-empty test (useEnsureStagedSuite).
// When the staged group holds unsaved work, a confirm gates the
// replacement first (same copy as the nav panel's replace-drop gate).
// While the staged suite group holds the selection (TESTS_EDITOR_ID),
// the face leads with the two ways to fill it — "Add a new test" (the
// suite row's Plus) and "Generate a test" (its Wand) — over the suite
// entry. An EMPTY staged suite always shows the "New test first" face
// instead (Figma 14790-130431): just "Write a test" (minting the
// untitled draft the panel's invitation row stands for) and "Generate a
// test" — no suite link.
function LinksFace() {
  const { editsLocked } = useStudioPipeline();
  const theme = useTheme();
  const {
    stagedSuite,
    tests,
    testEditorId,
    openNewTestDraft,
    hasDirtyTestWork,
    stagePendingSuite,
  } = usePipelineStaging();
  const { data: suites } = useTestSuites();
  const generation = useGenerateSuiteTests();
  const [naming, setNaming] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const openNaming = () =>
    chooseSuiteNamingAction(
      stagedSuite,
      hasDirtyTestWork,
      () => setConfirming(true),
      () => setNaming(true),
    );
  const emptySuite = isEmptyStagedSuite(stagedSuite, tests.length);

  return (
    <Panel data-testid="tests-editor-panel">
      {emptySuite ? (
        <>
          <IconLink
            as={GenerateLink}
            testId="tests-write-test-link"
            label="Write a test"
            color={
              editsLocked ? theme.semanticTextPlaceholder : theme.linkColor
            }
            disabled={editsLocked}
            onClick={openNewTestDraft}
          />
          <GenerateTestLink generation={generation} />
        </>
      ) : (
        <>
          <StagedSuiteLinks
            stagedSuite={stagedSuite}
            testEditorId={testEditorId}
            openNewTestDraft={openNewTestDraft}
            generation={generation}
          />
          <IconLink
            as={GenerateLink}
            testId="tests-add-suite-link"
            label="Add a new test suite"
            color={
              editsLocked ? theme.semanticTextPlaceholder : theme.linkColor
            }
            disabled={editsLocked}
            onClick={openNaming}
          />
        </>
      )}
      <ConfirmDeleteModal
        isOpen={confirming}
        title="Replace test suite"
        confirmLabel="Replace"
        message={
          <>
            The staged suite has unsaved changes that will be lost. Replace it
            anyway?
          </>
        }
        onConfirm={() => {
          setConfirming(false);
          setNaming(true);
        }}
        onCancel={() => setConfirming(false)}
      />
      {/* Add stages the named suite as a PENDING group — no catalog
          write, no tree row, until its first non-empty test saves. */}
      <NameModal
        isOpen={naming}
        onClose={() => setNaming(false)}
        onSave={async (name) => {
          stagePendingSuite(name);
        }}
        title={`Add a new test ${UNMATCHABLE_SUITE}`}
        placeholder={`Add a name to the test ${UNMATCHABLE_SUITE}`}
        submitLabel="Add"
        emptyMessage={`Add a name to the test ${UNMATCHABLE_SUITE}`}
        errorFallback="Failed to create test suite"
        testIdBase="add-test-suite"
        validateName={(trimmed) => suiteNameValidation(suites, trimmed)}
      />
    </Panel>
  );
}

// One Bridge_TestLine: field path, operator dropdown, expected value and
// the remove control. A new line lands with its field input focused (the
// active underline of the Figma mock).
function AssertionLine({
  row,
  onPatch,
  onRemove,
}: {
  row: TestAssertionDraft;
  onPatch: (patch: Partial<TestAssertionDraft>) => void;
  onRemove: () => void;
}) {
  const removeRef = useLabeledButton("Remove assertion");
  // The whole line freezes in the view-only studio (editsLocked).
  const { editsLocked } = useStudioPipeline();
  return (
    <Line data-testid="test-assertion-line">
      <UnderlineInput
        value={row.path}
        onChange={(e) => onPatch({ path: e.target.value })}
        placeholder="Add field"
        data-testid="test-field-input"
        disabled={editsLocked}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={!editsLocked}
      />
      <OperatorDropdown
        value={row.op}
        onChange={(op) => onPatch({ op })}
        disabled={editsLocked}
      />
      <UnderlineInput
        value={row.value}
        onChange={(e) => onPatch({ value: e.target.value })}
        placeholder="Add value"
        data-testid="test-value-input"
        disabled={editsLocked}
      />
      <ButtonSmallSubtleIcon
        ref={removeRef}
        icon={Icon.Bin}
        onClick={onRemove}
        disabled={editsLocked}
      />
    </Line>
  );
}

// The operator picker: a small secondary button with a chevron opening the
// operator menu (same trigger pattern as old /studio's DropdownButton, the
// menu items as AddService's SelectDropdown).
function OperatorDropdown({
  value,
  onChange,
  disabled = false,
}: {
  value: TestAssertionOp;
  onChange: (op: TestAssertionOp) => void;
  disabled?: boolean;
}) {
  const items: ListItemProps[] = OPERATOR_OPTIONS.map((option) => ({
    id: option.value,
    type: "text",
    value: option.value,
    label: option.label,
    closeOnSelect: true,
    selected: { value: option.value === value, icon: "dot" },
    onClick: () => onChange(option.value),
  }));
  return (
    <OperatorSlot>
      <GenericDropdown
        dropdownItems={[items]}
        width={{ strategy: "fixed", size: 120 }}
        disabled={disabled}
      >
        <GenericDropdownContext.Consumer>
          {({ open }) => (
            <GenericButton
              text={TEST_OPERATOR_LABELS[value]}
              type="secondary"
              size={ButtonSize.Small}
              secondaryIcon={Icon.BaseChevron}
              secondaryIconPosition="trailing"
              active={open}
              disabled={disabled}
            />
          )}
        </GenericDropdownContext.Consumer>
      </GenericDropdown>
    </OperatorSlot>
  );
}

// The wand link while a batch is in flight — the disabled link gray of
// the status bar controls, no hover underline.
const GenerateLink = styled(SampleLink)`
  &:disabled {
    color: ${(p) => p.theme.semanticTextPlaceholder};
    cursor: default;

    &:hover {
      text-decoration: none;
    }
  }
`;

// GenericDropdown's root is a plain block div — left alone it stretches
// across the line and shoves the value input to the far edge.
const OperatorSlot = styled.div`
  flex: 0 0 auto;
`;

const Panel = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${designVariables.ui.semantic.spacings}px;
  padding: ${designVariables.ui.semantic.spacingm}px
    ${designVariables.ui.semantic.spacingm}px 0;
`;

const Lines = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Line = styled.div`
  height: 36px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${designVariables.ui.semantic.spacings}px;
  padding: 0 ${designVariables.ui.semantic.spacings}px;
  background: ${(p) => p.theme.cards1Background};
`;

// The viewer's Bridge_TestLine: the editor row's geometry with the text
// rendered plain (Inter Medium 14, single line, clipped at the edge).
const ViewLine = styled(Line)`
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
  overflow: hidden;
`;

// The old /studio's empty-case wording, in the preview cards' italic
// placeholder voice, at the Bridge_TestLine height.
const EmptyViewerNote = styled.div`
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-style: italic;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextPlaceholder};
`;

const ViewDim = styled.span`
  flex-shrink: 0;
  color: ${(p) => p.theme.semanticTextLight};
`;

const ViewValue = styled.span`
  color: ${(p) => p.theme.semanticTextHeading};
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Figma TextInput (borderless variant): transparent body, hairline bottom
// border turning mainprimary while focused.
const UnderlineInput = styled.input`
  height: ${designVariables.ui.input.height}px;
  width: 140px;
  padding: 0 ${designVariables.ui.input.paddingw}px;
  border: none;
  border-bottom: 1px solid ${(p) => p.theme.inputDefaultBorder};
  background: transparent;
  font-size: 14px;
  line-height: 1.2;
  font-variant-numeric: lining-nums tabular-nums;
  color: ${(p) => p.theme.semanticTextBody};
  outline: none;

  &:focus {
    border-bottom-color: ${(p) => p.theme.inputActiveBorder};
  }

  &::placeholder {
    color: ${(p) => p.theme.semanticTextPlaceholder};
  }
`;

// semantic/spacingxl (40px) isn't exported by design-variables yet —
// hardcoded like the other missing tokens.
const AddAssertionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${designVariables.ui.semantic.spacings}px;
  padding: 40px ${designVariables.ui.semantic.spacings}px;
`;

const StrictToggle = styled.div`
  display: flex;
  align-items: center;
`;
